import { useParams, useNavigate } from 'react-router-dom';
import { usePedido, useAtualizarStatusPedido, useGerarEtiqueta } from '@/hooks/usePedidos';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { ArrowLeft } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useState } from 'react';
import type { StatusPedido } from '@/types';

const statusConfig: Record<StatusPedido, { label: string; variant: any }> = {
  NOVO: { label: 'Novo', variant: 'secondary' },
  AGUARDANDO_PAGAMENTO: { label: 'Aguard. Pagamento', variant: 'warning' },
  PAGO_CONFIRMADO: { label: 'Pago/Confirmado', variant: 'info' },
  PRODUCAO: { label: 'Em Produção', variant: 'warning' },
  AGUARDANDO_SEPARACAO: { label: 'A Separar', variant: 'warning' },
  EM_SEPARACAO: { label: 'Em Separação', variant: 'warning' },
  PRONTO_EXPEDICAO: { label: 'Pronto Entrega', variant: 'success' },
  ENVIADO: { label: 'Enviado', variant: 'info' },
  ENTREGUE: { label: 'Entregue', variant: 'success' },
  CANCELADO: { label: 'Cancelado', variant: 'error' },
};

import { Printer } from 'lucide-react';

export function PedidoDetalhe() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: pedido, isLoading } = usePedido(id!);
  const atualizarStatus = useAtualizarStatusPedido();
  const gerarEtiqueta = useGerarEtiqueta();
  const [novoStatus, setNovoStatus] = useState<StatusPedido>('' as StatusPedido);

  const handleAtualizarStatus = async () => {
    if (novoStatus) {
      await atualizarStatus.mutateAsync({ id: id!, status: novoStatus });
    }
  };

  if (isLoading) {
    return <div className="text-gray-900">Carregando...</div>;
  }

  if (!pedido) {
    return <div className="text-gray-900">Pedido não encontrado</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pedido {pedido.numeroPedido}</h1>
            <p className="text-gray-500">Criado em {formatDate(pedido.createdAt)}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm" 
            leftIcon={<Printer className="h-4 w-4" />}
            onClick={() => window.print()}
          >
            Imprimir Romaneio
          </Button>
          <Badge variant={statusConfig[pedido.status].variant} className="text-base px-4 py-1">
            {statusConfig[pedido.status].label}
          </Badge>
          
          {(pedido.status === 'PRONTO_EXPEDICAO' || pedido.status === 'AGUARDANDO_SEPARACAO') && !pedido.etiquetaUrl && (
            <Button 
              variant="nuvem" 
              size="sm"
              onClick={() => gerarEtiqueta.mutate(pedido.id)}
              isLoading={gerarEtiqueta.isPending}
            >
              Gerar Etiqueta (Melhor Envio)
            </Button>
          )}

          {pedido.etiquetaUrl && (
            <Button 
              variant="outline" 
              size="sm"
              className="text-green-600 border-green-200 hover:bg-green-50"
              onClick={() => window.open(pedido.etiquetaUrl, '_blank')}
            >
              Ver Etiqueta
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Itens do Pedido</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pedido.items?.map((item: any) => (
                <div key={item.id} className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
                  <div>
                    <p className="font-medium text-gray-900">{item.produto?.nome}</p>
                    <p className="text-sm text-gray-500">Qtd: {item.quantidade}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-900">{formatCurrency(item.precoUnitario)}</p>
                    <p className="text-sm text-gray-500">
                      Total: {formatCurrency(item.precoUnitario * item.quantidade)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-2 border-t border-gray-200 pt-4">
              <div className="flex justify-between text-gray-500">
                <span>Subtotal</span>
                <span>{formatCurrency(pedido.faturamentoTotal)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Frete ({pedido.freteModal})</span>
                <span>{formatCurrency(pedido.freteValor)}</span>
              </div>
              {pedido.descontoAplicado > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Desconto</span>
                  <span>-{formatCurrency(pedido.descontoAplicado)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold text-gray-900">
                <span>Total</span>
                <span>{formatCurrency(pedido.faturamentoTotal + pedido.freteValor - pedido.descontoAplicado)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Cliente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="font-medium text-gray-900">{pedido.cliente?.nome}</p>
              <p className="text-sm text-gray-500">{pedido.cliente?.email}</p>
              <p className="text-sm text-gray-500">{pedido.cliente?.telefone}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Atualizar Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select value={novoStatus} onValueChange={(v) => setNovoStatus(v as StatusPedido)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o novo status" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="nuvem"
                className="w-full"
                onClick={handleAtualizarStatus}
                isLoading={atualizarStatus.isPending}
                disabled={!novoStatus || novoStatus === pedido.status}
              >
                Atualizar Status
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
